import { motion } from 'framer-motion';
import { BookOpen, Shield, Users, Target, Sparkles, CheckCircle2, PhoneCall, Network, ClipboardCheck, PackageCheck } from 'lucide-react';
import Header from '../layout/Header';

const formations = [
  {
    id: 1,
    title: "Cybersécurité: Comment choisir un bon mot de passe",
    category: "Sécurité",
    icon: Shield,
    description: "Apprenez les meilleures pratiques pour créer et gérer des mots de passe sécurisés qui protègent vos comptes et données sensibles.",
    color: "from-red-500 to-orange-500"
  },
  {
    id: 2,
    title: "Comment bien utiliser Office 365",
    category: "Productivité",
    icon: Users,
    description: "Maîtrisez les outils essentiels d'Office 365 pour optimiser votre productivité et collaborer efficacement avec votre équipe.",
    color: "from-blue-500 to-cyan-500"
  },
  {
    id: 3,
    title: "Petits hints sur Teams",
    category: "Communication",
    icon: Target,
    description: "Découvrez les astuces et fonctionnalités avancées de Microsoft Teams pour une communication optimale en équipe.",
    color: "from-purple-500 to-pink-500"
  },
  {
    id: 4,
    title: "Formation charge de projet",
    category: "Gestion",
    icon: BookOpen,
    description: "Apprenez à gérer efficacement la charge de projet, optimiser les ressources et respecter les échéances.",
    color: "from-green-500 to-emerald-500"
  }
];

const videoUrl = "https://app.heygen.com/embeds/6ca8b210477c4ad8b01054c99d969aa9";

const telecomSalesTracks = [
  {
    title: "Téléphonie hébergée",
    icon: PhoneCall,
    tone: "blue",
    purpose: "Vendre une solution de communication fiable, évolutive et simple à gérer pour remplacer ou moderniser la téléphonie du client.",
    process: [
      "Qualifier le contexte: nombre d'utilisateurs, sites, numéros, postes, réception, files d'appel, horaires et contraintes actuelles.",
      "Identifier les douleurs: coûts, pannes, appels manqués, transfert d'appels, télétravail, mobilité, rapports et administration.",
      "Valider l'environnement réseau: lien Internet, pare-feu, commutateurs, VLAN voix, PoE, qualité de service et redondance.",
      "Présenter l'architecture cible: utilisateurs, extensions, groupes d'appel, menus vocaux, messagerie vocale, enregistrements et options mobiles.",
      "Préparer la proposition: forfaits, équipements, portabilité, installation, formation, support et échéancier de transition.",
      "Planifier la mise en service: collecte des informations, test des appels, portabilité des numéros, formation et suivi après activation."
    ],
    products: [
      "Lignes et utilisateurs de téléphonie hébergée",
      "Téléphones IP, bases sans-fil DECT et accessoires",
      "Menus vocaux, files d'appel, groupes d'appel et règles horaires",
      "Messagerie vocale vers courriel et options de mobilité",
      "Portabilité des numéros et configuration des appels entrants/sortants",
      "Services d'installation, formation et support continu"
    ]
  },
  {
    title: "Câblage structuré et fibre optique",
    icon: Network,
    tone: "emerald",
    purpose: "Vendre une infrastructure physique propre, certifiable et prête pour la croissance réseau, Wi-Fi, téléphonie, caméras et équipements critiques.",
    process: [
      "Faire la prise de besoin: plans, nombre de prises, types d'équipements, zones de travail, salle serveur et contraintes de bâtiment.",
      "Valider le site: parcours de câbles, plafonds, conduits, distances, obstacles, normes, heures de travail et accès aux locaux.",
      "Définir la solution: cuivre Cat6/Cat6A, fibre monomode ou multimode, panneaux, cabinets, rack, gestion de câbles et identification.",
      "Évaluer les risques: distance maximale, environnement électrique, travaux en hauteur, perçage, interruption client et besoin de sous-traitance.",
      "Produire la soumission: matériel, main-d'oeuvre, certification, documentation, photos, plans de repérage et exclusions clairement nommées.",
      "Livrer et fermer: installation, tests, étiquetage, certification, remise de documentation et validation finale avec le client."
    ],
    products: [
      "Câblage structuré cuivre Cat6 et Cat6A",
      "Fibre optique monomode ou multimode",
      "Panneaux de brassage, prises murales, keystones et cordons",
      "Racks, cabinets, gestionnaires de câbles et accessoires",
      "Tests, certification, étiquetage et documentation",
      "Installation, déplacement, ajout et nettoyage de câblage existant"
    ]
  }
];

const telephonyGlossary = [
  {
    term: "Téléphonie hébergée",
    plain: "Le système téléphonique n'est plus une grosse boîte chez le client: il est hébergé dans le nuage et géré à distance."
  },
  {
    term: "VoIP",
    plain: "La voix passe par Internet ou par le réseau informatique au lieu d'une ligne téléphonique traditionnelle."
  },
  {
    term: "PBX",
    plain: "Le cerveau du système téléphonique: il gère les extensions, les transferts, les menus et les règles d'appels."
  },
  {
    term: "Extension",
    plain: "Le numéro interne d'un utilisateur ou d'un poste, par exemple 201 pour rejoindre la réception."
  },
  {
    term: "DID / numéro direct",
    plain: "Un numéro public qui sonne directement à une personne, une équipe ou un service sans passer par la réception."
  },
  {
    term: "Portabilité",
    plain: "Le transfert des numéros actuels du client vers la nouvelle solution, sans changer ses numéros connus."
  },
  {
    term: "Menu vocal / IVR",
    plain: "Le message automatisé du type: appuyez sur 1 pour les ventes, 2 pour le support, 3 pour l'administration."
  },
  {
    term: "File d'appel",
    plain: "Une ligne d'attente intelligente qui distribue les appels à plusieurs employés selon des règles définies."
  },
  {
    term: "Softphone",
    plain: "Une application sur ordinateur ou cellulaire qui permet de faire et recevoir des appels comme avec un téléphone de bureau."
  },
  {
    term: "PoE",
    plain: "Le courant électrique passe dans le câble réseau; pratique pour alimenter un téléphone IP sans bloc d'alimentation."
  },
  {
    term: "QoS",
    plain: "Une priorité donnée à la voix sur le réseau pour éviter les coupures, délais ou appels qui sonnent comme une canne de conserve."
  },
  {
    term: "SIP",
    plain: "Le protocole utilisé par plusieurs systèmes VoIP pour établir et gérer les appels."
  }
];

const hostedTelephonyUseCases = [
  "Éliminer les coûts liés à un serveur téléphonique local",
  "Réduire les frais de maintenance",
  "Permettre le télétravail",
  "Offrir des fonctionnalités avancées sans investissement important",
  "Simplifier l'ajout de nouveaux utilisateurs",
  "Centraliser toutes les communications de l'entreprise"
];

const hostedTelephonyAdvantages = [
  {
    title: "Flexibilité",
    text: "Les employés peuvent utiliser un téléphone IP, un ordinateur, un téléphone intelligent ou une tablette tout en conservant leur numéro professionnel."
  },
  {
    title: "Évolutivité",
    text: "L'ajout d'un nouvel employé prend généralement quelques minutes, sans acheter une nouvelle centrale téléphonique."
  },
  {
    title: "Haute disponibilité",
    text: "Les plateformes sont généralement hébergées dans plusieurs centres de données pour rediriger les appels si un serveur devient indisponible."
  },
  {
    title: "Coûts prévisibles",
    text: "La majorité des fournisseurs fonctionnent avec un abonnement mensuel par utilisateur, ce qui facilite la planification budgétaire."
  }
];

const sipLineAdvantages = [
  "Excellente qualité audio",
  "Réduction des coûts",
  "Appels simultanés",
  "Appels internationaux moins coûteux",
  "Grande flexibilité"
];

const popularTelephonyFeatures = [
  {
    title: "Standard automatique / IVR",
    text: "Le système accueille automatiquement les appelants et les dirige vers le bon département.",
    example: "Bonjour et bienvenue. Pour les ventes, appuyez sur 1. Pour le soutien technique, appuyez sur 2."
  },
  {
    title: "File d'attente",
    text: "Lorsque tous les agents sont occupés, les appels restent en attente avec une musique, puis sont distribués dès qu'un agent devient disponible."
  },
  {
    title: "Groupes de sonnerie",
    text: "Plusieurs téléphones peuvent sonner simultanément ou selon un ordre précis, par exemple réception, service technique, puis comptabilité."
  },
  {
    title: "Messagerie vocale",
    text: "Chaque utilisateur possède sa boîte vocale. Les messages peuvent être envoyés par courriel, consultés au téléphone ou écoutés depuis l'application mobile."
  },
  {
    title: "Application mobile",
    text: "Les employés peuvent répondre, transférer, consulter leur messagerie et appeler avec leur numéro professionnel, même à l'extérieur du bureau."
  },
  {
    title: "Télétravail",
    text: "Les employés travaillent de la maison sans VPN complexe et gardent les mêmes fonctionnalités qu'au bureau."
  },
  {
    title: "Enregistrement des appels",
    text: "Les appels peuvent être enregistrés pour la formation, l'assurance qualité ou des exigences légales, selon les lois applicables et les avis nécessaires."
  },
  {
    title: "Conférence téléphonique",
    text: "Permet d'organiser des réunions audio avec plusieurs participants."
  },
  {
    title: "Vidéoconférence",
    text: "Certaines plateformes permettent aussi les appels vidéo et le partage d'écran."
  },
  {
    title: "Présence des utilisateurs",
    text: "Permet de voir si un collègue est disponible, en appel, absent, en réunion ou en mode ne pas déranger."
  },
  {
    title: "Transfert d'appel",
    text: "Les appels peuvent être transférés à un collègue, envoyés vers un cellulaire ou dirigés vers une autre succursale."
  },
  {
    title: "Horaire d'ouverture",
    text: "Le système adapte son comportement selon l'heure: réception le jour, messagerie le soir, message personnalisé les jours fériés."
  },
  {
    title: "Statistiques et rapports",
    text: "Les gestionnaires peuvent suivre le nombre d'appels, le temps d'attente, le taux de réponse, la durée moyenne et les performances des agents."
  }
];

const bzTelecomTurnkeyServices = [
  "Analyse de vos besoins",
  "Configuration complète du système",
  "Portabilité de vos numéros existants",
  "Installation des téléphones IP",
  "Formation des utilisateurs",
  "Maintenance et mises à jour",
  "Support technique local",
  "Surveillance proactive de la plateforme"
];

const bzTelecomPlatformFeatures = [
  "Standard téléphonique automatisé (IVR)",
  "Files d'attente intelligentes",
  "Groupes de sonnerie",
  "Boîtes vocales personnalisées",
  "Transfert d'appels",
  "Renvoi intelligent",
  "Téléphones IP",
  "Applications mobiles",
  "Softphone pour ordinateur",
  "Conférences téléphoniques",
  "Présence des utilisateurs",
  "Messagerie vocale par courriel",
  "Transcription des messages vocaux",
  "Rapports détaillés",
  "Enregistrement des appels",
  "Intégration avec Microsoft Teams et plusieurs applications d'affaires, selon les besoins"
];

const bzTelecomAiFeatures = [
  "Transcription automatique des appels",
  "Transcription des messages vocaux",
  "Résumés intelligents des conversations",
  "Évaluation de la qualité des appels",
  "Analyse des performances des agents",
  "Recherche par mots-clés dans les appels enregistrés",
  "Automatisation de certaines tâches administratives"
];

const bzTelecomIdealFor = [
  "Entreprises possédant plusieurs succursales",
  "Équipes en télétravail",
  "Centres de contact",
  "Entreprises de services",
  "Commerces",
  "Organismes publics",
  "Entreprises en croissance"
];

const bzTelecomInfrastructure = [
  "Infrastructure redondante",
  "Sauvegardes régulières",
  "Communications sécurisées",
  "Surveillance proactive des services",
  "Mises à jour effectuées par nos équipes"
];

const bzTelecomReasons = [
  "Un accompagnement local qui comprend la réalité des PME",
  "Une solution clé en main, de l'analyse au support",
  "Une expertise télécom combinée à l'expertise TI de BZ",
  "Une plateforme évolutive qui accompagne la croissance",
  "Des outils modernes pour améliorer l'expérience client et la mobilité des employés"
];

const bzTelecomWhyParagraphs = [
  "En choisissant la téléphonie hébergée de BZ Telecom, vous profitez d'un partenaire québécois qui vous accompagne à chaque étape de votre projet. Notre approche va bien au-delà de la simple fourniture d'un service téléphonique: nous analysons vos besoins, configurons une solution adaptée à votre entreprise, assurons la migration de vos numéros, formons vos utilisateurs et demeurons disponibles pour vous accompagner au quotidien.",
  "Grâce à notre expertise en téléphonie IP, en centres de contact et en intelligence artificielle, nous offrons une plateforme évolutive qui améliore les communications, augmente la productivité des équipes et contribue à offrir une expérience client de qualité. Avec un service local, une maintenance incluse, des technologies de pointe et des fonctionnalités avancées, la téléphonie hébergée de BZ Telecom constitue une solution complète, fiable et prête à accompagner la croissance de votre entreprise."
];

const bzTelecomRelatedProducts = [
  {
    title: "Plateforme SMS centralisée",
    description: "La plateforme SMS centralisée de BZ Telecom permet aux entreprises de gérer l'ensemble de leurs communications par messages texte à partir d'une interface unique. Plusieurs employés peuvent utiliser un même numéro d'entreprise tout en conservant l'historique complet des conversations.",
    note: "Cette solution facilite les échanges avec les clients, améliore le suivi des communications et assure une réponse rapide, peu importe l'employé qui prend en charge la conversation.",
    features: [
      "Boîte de réception partagée pour toute l'équipe",
      "Historique complet des conversations",
      "Réponses simultanées par plusieurs agents",
      "Messages automatisés et modèles de réponses",
      "Notifications en temps réel",
      "Accès depuis un ordinateur ou un appareil mobile",
      "Conservation de l'historique des échanges",
      "Intégration avec les systèmes téléphoniques et CRM, selon les besoins"
    ]
  },
  {
    title: "Système évolué de rapports de files d'attente",
    description: "Le système évolué de rapports de files d'attente de BZ Telecom fournit une vision complète de la performance de votre centre d'appels. Grâce à des tableaux de bord détaillés et à des rapports personnalisés, les gestionnaires suivent les indicateurs clés afin d'améliorer le service à la clientèle et la productivité des équipes.",
    note: "Ces informations permettent d'identifier rapidement les périodes de forte affluence, d'ajuster les horaires et d'optimiser les ressources.",
    features: [
      "Nombre d'appels reçus et répondus",
      "Appels abandonnés",
      "Temps moyen d'attente",
      "Temps moyen de conversation",
      "Temps de traitement après appel",
      "Niveau de service (SLA)",
      "Performance individuelle des agents",
      "Performance par équipe",
      "Occupation des agents",
      "Rapports en temps réel et historiques",
      "Exportation Excel, PDF et CSV"
    ]
  },
  {
    title: "EVA - Plateforme intelligente d'enregistrement des appels",
    description: "EVA est la solution avancée de BZ Telecom dédiée à l'enregistrement, à l'analyse et à l'amélioration de la qualité des communications téléphoniques. Elle combine un enregistrement sécurisé à long terme avec des outils d'intelligence artificielle pour transformer chaque appel en information exploitable.",
    note: "Contrairement à un simple système d'enregistrement, EVA aide les gestionnaires à évaluer la qualité du service, à former les équipes et à identifier les occasions d'amélioration.",
    features: [
      "Enregistrement longue durée selon les politiques internes ou obligations réglementaires",
      "Recherche rapide par numéro, agent, client, date ou durée de l'appel",
      "Transcription automatique des conversations",
      "Résumé intelligent avec motif, points abordés, décisions, actions et suivis",
      "Évaluation automatisée des agents",
      "Recherche intelligente par mot-clé, dossier, client ou sujet",
      "Tableaux de bord sur la qualité, les tendances et les performances"
    ],
    details: [
      {
        title: "Critères d'évaluation possibles",
        items: [
          "Qualité de l'accueil",
          "Respect du script de l'entreprise",
          "Professionnalisme",
          "Courtoisie et empathie",
          "Résolution du problème",
          "Respect des procédures internes",
          "Conformité réglementaire"
        ]
      }
    ]
  },
  {
    title: "Service de calendrier d'urgence",
    description: "Le service de calendrier d'urgence de BZ Telecom permet de gérer automatiquement les horaires d'urgence, les gardes et les périodes de disponibilité du personnel. En cas d'appel reçu en dehors des heures normales ou lors d'une situation particulière, le système dirige automatiquement les appels vers les personnes responsables selon un calendrier prédéfini.",
    note: "Cette solution réduit les interventions manuelles, assure une prise en charge rapide des appels critiques et garantit que le bon intervenant est joint au bon moment.",
    features: [
      "Gestion des horaires de garde",
      "Acheminement intelligent des appels selon la date et l'heure",
      "Escalade automatique vers un second intervenant si le premier ne répond pas",
      "Notification par appel, SMS ou courriel",
      "Modification des horaires en temps réel",
      "Consultation des calendriers à distance",
      "Historique des interventions et des changements de garde"
    ],
    audience: "Particulièrement adapté aux entreprises offrant un service d'urgence ou un soutien 24/7: services techniques, maintenance, transport, santé ou tout secteur nécessitant une disponibilité continue."
  },
  {
    title: "Transcription des messages vocaux",
    description: "La transcription des messages vocaux convertit automatiquement un message laissé dans une boîte vocale en texte grâce à l'intelligence artificielle. L'utilisateur reçoit une version écrite dans son courriel, son application mobile ou son portail de téléphonie.",
    note: "Cette fonctionnalité permet de traiter les demandes plus rapidement, retrouver un message en quelques secondes et offrir un meilleur service à la clientèle, tout en conservant l'enregistrement audio original.",
    features: [
      "Lecture rapide des messages sans les écouter",
      "Consultation discrète en réunion ou dans un lieu public",
      "Recherche de mots-clés dans les messages",
      "Archivage simplifié des communications",
      "Gain de temps pour les utilisateurs",
      "Meilleur suivi des demandes clients"
    ],
    details: [
      {
        title: "Comment cela fonctionne?",
        items: [
          "Un client laisse un message vocal",
          "Le système enregistre le message",
          "L'intelligence artificielle analyse l'enregistrement",
          "Le message est converti en texte",
          "L'utilisateur reçoit le fichier audio, la transcription et, selon la configuration, un résumé"
        ]
      },
      {
        title: "Exemple de résultat",
        items: [
          "Message: Bonjour, ici Jean Tremblay de Construction ABC. Je vous appelle concernant notre rendez-vous de demain matin. Merci de me rappeler au 418-555-1234.",
          "Résumé: Client Jean Tremblay, Construction ABC. Sujet: confirmation d'un rendez-vous. Action recommandée: rappeler le client au 418-555-1234."
        ]
      }
    ],
    audience: "Particulièrement utile pour les équipes de vente, le soutien technique, les services d'urgence et toute entreprise recevant un volume important d'appels."
  }
];

export default function Formation() {
  return (
    <div className="min-h-screen">
      <Header 
        title="Formation" 
        subtitle="Développer en continu les compétences de toute l'équipe BZ, tous départements confondus"
      />
      
      <div className="px-8 pb-12">
        {/* Section Pédagogique - Comment ça fonctionne */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl p-8 border-2 border-indigo-200 shadow-lg"
        >
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-4 rounded-xl shadow-lg">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                La Formation au Cœur de l'Excellence BZ
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Cette page regroupera, au fil du temps, <strong className="text-indigo-700">toutes les formations internes BZ</strong> 
                qui aident chaque employé à mieux faire son travail, peu importe son département (Service TI, Ventes, Cybersécurité,
                Infonuagique, Programmation & IA, Administration & RH, etc.). 
                <strong className="text-purple-700"> Notre objectif : rendre la connaissance accessible, structurée et vivante.</strong>
              </p>
              <div className="bg-white/60 rounded-xl p-6 border border-indigo-200">
                <h3 className="text-xl font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  Comment cette page évoluera?
                </h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-indigo-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                    <span>Les besoins de formation sont identifiés par les <strong className="text-indigo-700">directeurs de départements, les gestionnaires et les employés eux‑mêmes</strong>.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                    <span><strong className="text-purple-700">PRIsME et l'IA analysent</strong> ces besoins et suggèrent des contenus de formation pertinents (vidéos, capsules, guides, parcours complet).</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                    <span><strong className="text-pink-700">Les responsables de contenu BZ</strong> sélectionnent, ajustent et valident les formations selon la réalité du terrain.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                    <span><strong className="text-green-700">Les formations apparaissent ici</strong>, avec à terme un système de filtres (par département, type, niveau, obligatoire / recommandé) 
                    et un suivi par employé (progression, complétion, certification).</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Message d'Exemple */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl p-6 shadow-lg"
        >
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-6 h-6" />
            <h3 className="text-xl font-bold">Exemple de Formations Disponibles</h3>
          </div>
          <p className="text-blue-100">
            Les formations ci-dessous sont des <strong className="text-white">exemples de capsules internes BZ</strong> 
            (cybersécurité, Office 365, Teams, charge de projet). 
            <strong className="text-white"> À terme, cette page affichera un catalogue complet de formations pour tous les employés BZ, filtrable par département et type de contenu.</strong>
          </p>
        </motion.div>

        {/* Grille des 4 Formations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
          >
            <div className="bg-gradient-to-r from-slate-900 via-blue-800 to-emerald-700 p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                  <PackageCheck className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <span className="text-sm font-semibold text-white/85 mb-1 block">Ventes Solutions Affaires</span>
                  <h3 className="text-2xl font-bold leading-tight">
                    Téléphonie hébergée, câblage structuré et fibre optique
                  </h3>
                  <p className="mt-3 text-white/85 leading-relaxed">
                    Référence de formation pour standardiser la prise de besoin, la qualification, la proposition et la livraison
                    des solutions de communication et d'infrastructure physique vendues par BZ.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                {telecomSalesTracks.map((track) => {
                  const Icon = track.icon;
                  const isBlue = track.tone === "blue";

                  return (
                    <section
                      key={track.title}
                      className={`rounded-xl border p-5 ${
                        isBlue
                          ? "border-blue-200 bg-blue-50/70"
                          : "border-emerald-200 bg-emerald-50/70"
                      }`}
                    >
                      <div className="flex items-start gap-3 mb-4">
                        <div className={`p-3 rounded-xl ${
                          isBlue ? "bg-blue-600 text-white" : "bg-emerald-600 text-white"
                        }`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-900">{track.title}</h4>
                          <p className="text-sm text-gray-700 leading-relaxed mt-1">{track.purpose}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <ClipboardCheck className={`w-5 h-5 ${isBlue ? "text-blue-600" : "text-emerald-600"}`} />
                            <h5 className="font-semibold text-gray-900">Processus de vente</h5>
                          </div>
                          <ol className="space-y-2 text-sm text-gray-700">
                            {track.process.map((step, stepIndex) => (
                              <li key={step} className="flex gap-2">
                                <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${
                                  isBlue ? "bg-blue-600" : "bg-emerald-600"
                                }`}>
                                  {stepIndex + 1}
                                </span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>
                        </div>

                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <PackageCheck className={`w-5 h-5 ${isBlue ? "text-blue-600" : "text-emerald-600"}`} />
                            <h5 className="font-semibold text-gray-900">Produits vendus</h5>
                          </div>
                          <ul className="space-y-2 text-sm text-gray-700">
                            {track.products.map((product) => (
                              <li key={product} className="flex gap-2">
                                <CheckCircle2 className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                                  isBlue ? "text-blue-600" : "text-emerald-600"
                                }`} />
                                <span>{product}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </section>
                  );
                })}
              </div>

              <div className="mt-6 rounded-xl border border-blue-200 bg-white p-5">
                <div className="flex items-start gap-3 mb-5">
                  <div className="bg-blue-700 text-white p-3 rounded-xl">
                    <PhoneCall className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">Wiki de téléphonie</h4>
                    <p className="text-sm text-gray-700 leading-relaxed mt-1">
                      Base de connaissances pour vulgariser les concepts de téléphonie hébergée et aider l'équipe à mieux
                      expliquer la solution aux clients.
                    </p>
                  </div>
                </div>

                <div className="space-y-5">
                  <article className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                    <h5 className="text-lg font-bold text-slate-900">La téléphonie hébergée: qu'est-ce que c'est?</h5>
                    <div className="mt-3 space-y-3 text-sm text-slate-700 leading-relaxed">
                      <p>
                        La <strong>téléphonie hébergée</strong>, aussi appelée <strong>Hosted PBX</strong> ou <strong>Cloud PBX</strong>,
                        est un système téléphonique d'entreprise dont l'infrastructure est installée dans un centre de données
                        sécurisé plutôt que dans les locaux de l'entreprise.
                      </p>
                      <p>
                        Au lieu d'acheter et d'entretenir un serveur téléphonique sur place, l'entreprise utilise une plateforme
                        gérée par son fournisseur. Les téléphones IP, les applications mobiles et les softphones se connectent à
                        cette plateforme par Internet.
                      </p>
                      <p>
                        En pratique, les employés peuvent répondre à leurs appels depuis le bureau, la maison ou leur téléphone
                        mobile tout en utilisant le même numéro professionnel.
                      </p>
                    </div>
                  </article>

                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <article className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                      <h5 className="text-lg font-bold text-slate-900">À quoi sert la téléphonie hébergée?</h5>
                      <ul className="mt-3 space-y-2 text-sm text-slate-700">
                        {hostedTelephonyUseCases.map((useCase) => (
                          <li key={useCase} className="flex gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
                            <span>{useCase}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-4 text-sm text-slate-700 leading-relaxed">
                        Elle convient autant à une petite entreprise de cinq employés qu'à une organisation de plusieurs centaines
                        d'utilisateurs répartis sur différents sites.
                      </p>
                    </article>

                    <article className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                      <h5 className="text-lg font-bold text-slate-900">Les avantages principaux</h5>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {hostedTelephonyAdvantages.map((advantage) => (
                          <div key={advantage.title} className="rounded-lg border border-blue-100 bg-white p-3">
                            <h6 className="text-sm font-bold text-blue-900">{advantage.title}</h6>
                            <p className="mt-1 text-sm text-slate-700 leading-relaxed">{advantage.text}</p>
                          </div>
                        ))}
                      </div>
                    </article>
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                    <article className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                      <h5 className="text-lg font-bold text-slate-900">Portabilité des numéros</h5>
                      <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                        La <strong>portabilité</strong> est le droit de conserver ses numéros lorsqu'on change de fournisseur.
                        Les clients continuent d'appeler les mêmes numéros, les cartes professionnelles demeurent valides et le
                        changement devient presque transparent.
                      </p>
                      <div className="mt-3 rounded-lg bg-white border border-slate-200 p-3 text-sm text-slate-700">
                        <div className="font-semibold text-slate-900 mb-1">Exemple</div>
                        <p>418-555-1000, 418-555-1001 et 418-555-1099 peuvent être transférés vers le nouveau service.</p>
                      </div>
                    </article>

                    <article className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                      <h5 className="text-lg font-bold text-slate-900">Fournisseur téléphonique</h5>
                      <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                        Le <strong>fournisseur</strong> est l'entreprise qui offre le service téléphonique. Il est responsable de
                        l'acheminement des appels, de l'hébergement de la plateforme, de la gestion des numéros, du soutien
                        technique et de la qualité du réseau.
                      </p>
                    </article>

                    <article className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                      <h5 className="text-lg font-bold text-slate-900">Ligne SIP</h5>
                      <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                        Une <strong>ligne SIP</strong> est la connexion qui permet au système téléphonique de communiquer avec le
                        réseau téléphonique public par Internet. Une seule connexion peut transporter plusieurs appels simultanés.
                      </p>
                      <div className="mt-3 rounded-lg bg-white border border-slate-200 p-3 text-sm text-slate-700">
                        <div className="font-semibold text-slate-900 mb-1">Exemple</div>
                        <p>Une entreprise de 20 employés avec 8 appels simultanés peut utiliser un forfait SIP de 8 canaux.</p>
                      </div>
                      <ul className="mt-3 space-y-2 text-sm text-slate-700">
                        {sipLineAdvantages.map((advantage) => (
                          <li key={advantage} className="flex gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
                            <span>{advantage}</span>
                          </li>
                        ))}
                      </ul>
                    </article>
                  </div>

                  <article className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                    <h5 className="text-lg font-bold text-slate-900">Fonctionnalités populaires</h5>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {popularTelephonyFeatures.map((feature) => (
                        <div key={feature.title} className="rounded-lg border border-blue-100 bg-white p-4">
                          <h6 className="text-sm font-bold text-blue-900">{feature.title}</h6>
                          <p className="mt-2 text-sm text-slate-700 leading-relaxed">{feature.text}</p>
                          {feature.example && (
                            <p className="mt-3 rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{feature.example}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </article>
                </div>
              </div>

              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="bg-slate-900 text-white p-3 rounded-xl">
                    <BookOpen className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">Lexique vulgarisé de téléphonie</h4>
                    <p className="text-sm text-gray-700 leading-relaxed mt-1">
                      Définitions simples pour aider les ventes à expliquer les termes techniques sans perdre le client dans le jargon.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {telephonyGlossary.map((item) => (
                    <div key={item.term} className="bg-white rounded-lg border border-slate-200 p-4">
                      <h5 className="text-sm font-bold text-slate-900">{item.term}</h5>
                      <p className="mt-2 text-sm text-slate-700 leading-relaxed">{item.plain}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
          >
            <div className="bg-gradient-to-r from-blue-700 via-cyan-700 to-slate-900 p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                  <PhoneCall className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <span className="text-sm font-semibold text-white/85 mb-1 block">Catalogue commercial</span>
                  <h3 className="text-2xl font-bold leading-tight">Produits que l'on vend</h3>
                  <p className="mt-3 text-white/85 leading-relaxed">
                    La téléphonie hébergée de BZ Telecom est une solution complète de communication d'entreprise qui permet
                    aux PME de bénéficier d'un système moderne, performant et évolutif, sans installer ni maintenir une
                    centrale téléphonique sur leurs propres serveurs.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <article className="rounded-xl border border-cyan-200 bg-cyan-50/60 p-5">
                <h4 className="text-xl font-bold text-gray-900">La téléphonie hébergée de BZ Telecom</h4>
                <div className="mt-3 space-y-3 text-sm text-gray-700 leading-relaxed">
                  <p>
                    La plateforme est hébergée dans une infrastructure sécurisée, redondante et administrée par BZ Telecom.
                    Les entreprises profitent ainsi des plus récentes technologies tout en éliminant les coûts et la complexité
                    liés à la gestion d'un PBX traditionnel.
                  </p>
                  <p>
                    Notre solution accompagne les entreprises dans leur croissance, améliore l'expérience client et offre une
                    grande mobilité aux employés. Que les équipes travaillent au bureau, à domicile ou sur la route, elles
                    disposent des mêmes fonctionnalités et utilisent leur numéro professionnel partout.
                  </p>
                </div>
              </article>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <article className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <h4 className="text-lg font-bold text-gray-900">Une solution clé en main</h4>
                  <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                    Chez BZ Telecom, nous nous occupons de l'ensemble de votre environnement téléphonique afin que vous puissiez
                    vous concentrer sur vos activités.
                  </p>
                  <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
                    {bzTelecomTurnkeyServices.map((service) => (
                      <li key={service} className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-cyan-700" />
                        <span>{service}</span>
                      </li>
                    ))}
                  </ul>
                </article>

                <article className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <h4 className="text-lg font-bold text-gray-900">Une plateforme pour les entreprises modernes</h4>
                  <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                    La solution rassemble les outils de communication nécessaires au fonctionnement d'une entreprise moderne.
                  </p>
                  <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
                    {bzTelecomPlatformFeatures.map((feature) => (
                      <li key={feature} className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-700" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <article className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-5 w-5 text-purple-700" />
                    <h4 className="text-lg font-bold text-gray-900">Téléphonie intelligente avec IA</h4>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    BZ Telecom intègre l'intelligence artificielle pour améliorer la qualité du service et donner de meilleurs
                    outils aux gestionnaires.
                  </p>
                  <ul className="mt-4 space-y-2 text-sm text-gray-700">
                    {bzTelecomAiFeatures.map((feature) => (
                      <li key={feature} className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-purple-700" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </article>

                <article className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="h-5 w-5 text-cyan-700" />
                    <h4 className="text-lg font-bold text-gray-900">Adaptée à votre façon de travailler</h4>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Peu importe où se trouvent les employés, ils peuvent répondre aux appels de l'entreprise avec le même niveau
                    de qualité et de professionnalisme.
                  </p>
                  <ul className="mt-4 space-y-2 text-sm text-gray-700">
                    {bzTelecomIdealFor.map((item) => (
                      <li key={item} className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-cyan-700" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </article>

                <article className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="h-5 w-5 text-slate-800" />
                    <h4 className="text-lg font-bold text-gray-900">Infrastructure fiable et sécurisée</h4>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Les services reposent sur une infrastructure infonuagique conçue pour assurer la disponibilité et la
                    continuité des opérations.
                  </p>
                  <ul className="mt-4 space-y-2 text-sm text-gray-700">
                    {bzTelecomInfrastructure.map((item) => (
                      <li key={item} className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-800" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              </div>

              <article className="rounded-xl border border-blue-200 bg-blue-50/70 p-5">
                <h4 className="text-lg font-bold text-gray-900">Pourquoi choisir BZ Telecom?</h4>
                <div className="mt-3 space-y-3 text-sm text-gray-700 leading-relaxed">
                  {bzTelecomWhyParagraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
                <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 text-sm text-gray-700">
                  {bzTelecomReasons.map((reason) => (
                    <li key={reason} className="flex gap-2 rounded-lg border border-blue-100 bg-white p-3">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-700" />
                      <span>{reason}</span>
                    </li>
                  ))}
                </ul>
              </article>

              <article className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex items-start gap-3 mb-5">
                  <div className="bg-slate-900 text-white p-3 rounded-xl">
                    <PackageCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">Produits rattachés</h4>
                    <p className="text-sm text-gray-700 leading-relaxed mt-1">
                      Définitions professionnelles utilisables dans une brochure, sur le site Web ou dans une présentation de BZ Telecom.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                  {bzTelecomRelatedProducts.map((product) => (
                    <section key={product.title} className="rounded-xl border border-slate-200 bg-white p-5">
                      <h5 className="text-lg font-bold text-gray-900">{product.title}</h5>
                      <div className="mt-3 space-y-3 text-sm text-gray-700 leading-relaxed">
                        <p>{product.description}</p>
                        <p>{product.note}</p>
                      </div>

                      <ul className="mt-4 space-y-2 text-sm text-gray-700">
                        {product.features.map((feature) => (
                          <li key={feature} className="flex gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-700" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {product.details?.map((detail) => (
                        <div key={detail.title} className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <h6 className="text-sm font-bold text-slate-900">{detail.title}</h6>
                          <ul className="mt-3 space-y-2 text-sm text-slate-700">
                            {detail.items.map((item) => (
                              <li key={item} className="flex gap-2">
                                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-700" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}

                      {product.audience && (
                        <p className="mt-4 rounded-lg border border-cyan-100 bg-cyan-50 p-3 text-sm text-cyan-900 leading-relaxed">
                          {product.audience}
                        </p>
                      )}
                    </section>
                  ))}
                </div>
              </article>
            </div>
          </motion.div>

          {formations.map((formation, index) => {
            const Icon = formation.icon;
            return (
              <motion.div
                key={formation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                {/* Header avec gradient */}
                <div className={`bg-gradient-to-r ${formation.color} p-6 text-white`}>
                  <div className="flex items-start gap-4">
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                      <Icon className="w-8 h-8" />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-semibold opacity-90 mb-1 block">{formation.category}</span>
                      <h3 className="text-xl font-bold leading-tight">
                        {formation.title}
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Description + Vidéo */}
                <div className="p-6">
                  <p className="text-gray-600 mb-4">
                    {formation.description}
                  </p>
                  
                  {/* Vidéo */}
                  <div className="bg-gray-900 rounded-lg overflow-hidden shadow-lg">
                    <div className="aspect-video">
                      <iframe
                        src={videoUrl}
                        title={formation.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                  </div>
                  
                  {/* Note pédagogique */}
                  <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg">
                    <p className="text-sm text-blue-800">
                      <strong>💡 Note:</strong> Cette vidéo illustre le format de formation. 
                      Le contenu réel s'adapte selon vos besoins spécifiques.
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Message Final */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white rounded-2xl p-8 shadow-2xl"
        >
          <div className="flex items-start gap-4">
            <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
              <Sparkles className="w-10 h-10" />
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-3">Formation Continue et Personnalisée</h3>
              <p className="text-lg text-white/90 leading-relaxed mb-4">
                Chez BZ, <strong>un employé bien formé est la clé</strong>. PRIsME deviendra notre plateforme centrale pour
                structurer, diffuser et suivre la formation interne: 
                filtres par département, parcours obligatoires, formations recommandées, et même des <strong>programmes de certification BZ</strong> 
                pour faire évoluer les compétences de chacun.
              </p>
              <div className="flex items-center gap-2 text-white/80">
                <CheckCircle2 className="w-5 h-5" />
                <span>Page vivante, toujours en adaptation selon les besoins réels de l'entreprise et de l'équipe.</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
